import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  Database,
  FileCode,
  Loader2,
  Rocket,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api');

type Step = 1 | 2 | 3 | 4;

export default function TenantCreationWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [tenantInfo, setTenantInfo] = useState<{
    corporateId: string;
    name: string;
  }>({
    corporateId: searchParams.get('id') || '',
    name: searchParams.get('name') || '',
  });

  const [orgYaml, setOrgYaml] = useState(
    `# Tenant Configuration (YAML)
# Target Tenant (Corporation)
tenant_id: "${searchParams.get('id') || 'T1234567890123'}"
name: "${searchParams.get('name') || 'New Corporation'}"

# Initial Admin User (Tenant Admin)
admin_user:
  email: "admin@example.com"
  name: "Initial Admin"
  initial_password: "ChangeMe123!"

# Organizations (Universities/Colleges) to create under this Tenant
organizations:
  - code: "F105110101051" # MEXT Code
    name: "Example University"
    type: "private"
    units:
      - code: "fac-001"
        pattern: "FACULTY"
        name: "Department of Examples"
`,
  );

  const [provisioningStatus, setProvisioningStatus] = useState<{ success: boolean } | null>(null);

  // --- Rendering ---

  const steps = [
    { id: 1, label: t('tenant.wizard.steps.verification'), icon: Building2 },
    { id: 2, label: t('tenant.wizard.steps.configuration'), icon: Database },
    { id: 3, label: t('tenant.wizard.steps.activate'), icon: Rocket },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Step Indicator */}
      <div className="mb-12">
        <div className="flex items-center justify-between relative">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -translate-y-1/2" />
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            let stepStyles = 'bg-white border-gray-100 text-gray-400';
            if (isActive) {
              stepStyles = 'bg-blue-600 border-blue-100 text-white shadow-lg scale-110';
            } else if (isCompleted) {
              stepStyles = 'bg-green-500 border-green-100 text-white';
            }

            return (
              <div key={step.id} className="relative z-10 flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 border-4 ${stepStyles}`}
                >
                  {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                </div>
                <span
                  className={`text-xs mt-3 font-bold transition-colors ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content Area */}
      <div className="min-h-[400px]">
        {currentStep === 1 && (
          <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-800">
                {t('tenant.wizard.verification.title')}
              </CardTitle>
              <CardDescription>{t('tenant.wizard.verification.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="corporateId">{t('tenant.wizard.verification.corporateIdLabel')}</Label>
                  <Input
                    id="corporateId"
                    placeholder={t('tenant.wizard.verification.corporateIdPlaceholder')}
                    className="font-mono text-lg py-6"
                    value={tenantInfo.corporateId}
                    onChange={(e) => setTenantInfo({ ...tenantInfo, corporateId: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 italic">{t('tenant.wizard.verification.note')}</p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="bg-gray-50 rounded-b-lg border-t mt-8 p-6">
              <Button
                onClick={async () => {
                  if (tenantInfo.corporateId) {
                    if (tenantInfo.corporateId.length === 13) {
                      setLoading(true);
                      try {
                        // US-A01: Autofill
                        const res = await fetch(
                          `${API_BASE}/master/autofill?type=corporate&id=${tenantInfo.corporateId}`,
                        );
                        if (res.ok) {
                          const data = (await res.json()) as { name?: string };
                          setTenantInfo((prev) => ({ ...prev, name: data.name || '' }));

                          // Consolidate YAML generation
                          const newYaml = `# Tenant Configuration (YAML)
# Target Tenant (Corporation)
tenant_id: "${tenantInfo.corporateId}"
name: "${data.name || 'New Corporation'}"
autofill: true

# Initial Admin User (Tenant Admin)
admin_user:
  email: "admin@example.com"
  name: "Initial Admin"
  initial_password: "ChangeMe123!"

# Organizations (Universities/Colleges) to create under this Tenant
organizations:
  - code: "F105110101051" # MEXT Code (Example)
    name: "${data.name || 'Example University'}"
    type: "private"
    units:
      - code: "fac-001"
        pattern: "FACULTY"
        name: "Department of Examples"
`;
                          setOrgYaml(newYaml);
                        }
                      } catch (e) {
                        console.error('Autofill failed', e);
                        // Continue anyway
                      } finally {
                        setLoading(false);
                        setCurrentStep(2);
                      }
                    } else {
                      setError(t('tenant.wizard.verification.errorLength'));
                    }
                  }
                }}
                disabled={loading || !tenantInfo.corporateId}
                className="ml-auto px-8 py-6 text-lg"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  t('tenant.wizard.verification.verifyButton')
                )}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {currentStep === 2 && (
          <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  {t('tenant.wizard.configuration.title')}
                </CardTitle>
                <CardDescription>{t('tenant.wizard.configuration.description')}</CardDescription>
              </div>
              <Button
                variant="ghost"
                className="text-blue-600 gap-2"
                onClick={() => {
                  setOrgYaml(`# Tenant Setup
tenant_id: "${tenantInfo.corporateId}"
billing:
  tier: "standard"
`);
                }}
              >
                <FileCode className="w-4 h-4" /> {t('tenant.wizard.configuration.loadTemplate')}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="relative group">
                <Label htmlFor="org-yaml" className="sr-only">
                  {t('tenant.wizard.configuration.yamlLabel')}
                </Label>
                <Textarea
                  id="org-yaml"
                  className="font-mono min-h-[400px] text-sm bg-gray-900 text-green-400 p-6 selection:bg-gray-700 rounded-xl"
                  value={orgYaml}
                  onChange={(e) => setOrgYaml(e.target.value)}
                  placeholder={t('tenant.wizard.configuration.placeholder')}
                />
                <div className="absolute top-4 right-4 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                  YAML Config
                </div>
              </div>
            </CardContent>
            <CardFooter className="bg-gray-50 rounded-b-lg border-t mt-8 p-6 flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(1)} className="px-6 py-6 border-2">
                <ArrowLeft className="mr-2 h-4 w-4" /> {t('tenant.wizard.configuration.back')}
              </Button>
              <Button onClick={() => setCurrentStep(3)} disabled={loading} className="px-8 py-6 text-lg">
                {t('tenant.wizard.configuration.review')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Activation view */}

        {(currentStep === 3 || currentStep === 4) && (
          <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
            <div className="bg-blue-600 h-2 bg-gradient-to-r from-blue-600 to-indigo-600" />
            <CardHeader className="text-center py-10">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-50 text-blue-600 mb-6 border-4 border-blue-100 animate-pulse">
                <Rocket className="w-10 h-10" />
              </div>
              <CardTitle className="text-3xl font-black text-gray-900">{t('tenant.wizard.activate.title')}</CardTitle>
              <CardDescription className="text-lg mt-2">
                {t('tenant.wizard.activate.descriptionPrefix')}
                <b>{tenantInfo.corporateId}</b>
                {t('tenant.wizard.activate.descriptionSuffix')}
                <br />
                {t('tenant.wizard.activate.descriptionAction')}
              </CardDescription>
            </CardHeader>
            <CardContent className="bg-gray-50 border-y py-12 px-10">
              <div className="max-w-md mx-auto space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">{t('tenant.wizard.activate.corporateId')}:</span>
                  <span className="font-mono font-bold text-gray-900">{tenantInfo.corporateId}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-10 flex flex-col gap-4">
              {provisioningStatus?.success ? (
                <div className="space-y-4 w-full">
                  <div className="bg-green-50 text-green-700 p-6 rounded-xl w-full text-center font-bold flex items-center justify-center gap-2 border-2 border-green-200">
                    <Check className="w-6 h-6" /> {t('tenant.wizard.activate.sent')}
                  </div>
                  <div className="p-4 bg-gray-900 text-gray-200 rounded text-xs font-mono overflow-auto max-h-40">
                    {t('tenant.wizard.activate.log')}
                  </div>
                  <Button onClick={() => navigate('/system/tenants')} className="w-full">
                    {t('tenant.wizard.activate.return')}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError(null);
                      // POST to /tenants/:id/activate
                      console.log('Activating Tenant:', tenantInfo.corporateId);
                      const res = await fetch(`${API_BASE}/tenants/${tenantInfo.corporateId}/activate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          yaml: orgYaml,
                        }),
                      });

                      const json = (await res.json()) as { error?: string };

                      if (!res.ok) throw new Error(json.error || 'Activation failed');

                      setProvisioningStatus({ success: true });
                    } catch (e: unknown) {
                      if (e instanceof Error) {
                        setError(e.message);
                      } else {
                        setError(t('tenant.wizard.activate.errorUnknown'));
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full py-8 text-xl font-black rounded-2xl shadow-2xl hover:scale-[1.02] transition-transform bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                      {t('tenant.wizard.activate.loading')}
                    </>
                  ) : (
                    t('tenant.wizard.activate.submit')
                  )}
                </Button>
              )}
              {!provisioningStatus?.success && (
                <Button variant="ghost" onClick={() => setCurrentStep(2)} className="text-gray-400">
                  {t('tenant.wizard.activate.backToConfig')}
                </Button>
              )}
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
}
